import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core';
import type { FileProcessingService } from '../file-processing-service';
import type { FileInfo, UploadResult, RecognitionResult } from '../../types/file-upload';
import { FileProcessStatus } from '../../enums';
import { FileUploadService } from '../file-upload-service';
import { FileRecognitionService } from '../file-recognition-service';
import { FileDeleteService } from '../file-delete-service';
import { CurrentTaskService } from '@/services/current-task/current-task-service';

/**
 * 生成文件ID
 */
function generateFileId(): string {
    return `file_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * 创建文件信息对象
 */
function createFileInfo(file: File, contentType: 'DISCLOSURE' | 'CLAIM'): FileInfo {
    return {
        id: generateFileId(),
        name: file.name,
        size: file.size,
        type: file.type,
        contentType,
        status: FileProcessStatus.IDLE,
        uploadProgress: 0,
        recognitionProgress: 0,
        originalFile: file,
    };
}

@injectable()
export class FileProcessingServiceImpl implements FileProcessingService {
    private _files = new Map<string, FileInfo>();

    // 事件发射器
    private _onFilesChangedEmitter = new Emitter<FileInfo[]>();

    // 只读事件接口
    readonly onFilesChanged = this._onFilesChangedEmitter.event;

    // eslint-disable-next-line max-params
    constructor(
        @inject(FileUploadService)
        private _uploadService: FileUploadService,
        @inject(FileRecognitionService)
        private _recognitionService: FileRecognitionService,
        @inject(FileDeleteService)
        private _fileDeleteService: FileDeleteService,
        @inject(CurrentTaskService)
        private _currentTaskService: CurrentTaskService,
    ) {}

    getFiles(): Map<string, FileInfo> {
        return new Map(this._files);
    }

    getAllFiles(): FileInfo[] {
        return Array.from(this._files.values());
    }

    getActiveUploads(): FileInfo[] {
        return Array.from(this._files.values()).filter(
            file => file.status === FileProcessStatus.UPLOADING ||
                   file.status === FileProcessStatus.RECOGNIZING,
        );
    }

    getFailedUploads(): FileInfo[] {
        return Array.from(this._files.values()).filter(
            file => file.status === FileProcessStatus.UPLOAD_FAILED ||
                   file.status === FileProcessStatus.RECOGNITION_FAILED,
        );
    }

    hasActiveUploads(): boolean {
        return this.getActiveUploads().length > 0;
    }

    async processFile(params: { file: File; contentType: 'DISCLOSURE' | 'CLAIM' }): Promise<string> {
        const { file, contentType } = params;
        const fileInfo = createFileInfo(file, contentType);
        this._files.set(fileInfo.id, fileInfo);
        this._onFilesChangedEmitter.fire(this.getAllFiles());

        try {
            // 第一阶段：上传
            this._updateFileStatus(fileInfo.id, FileProcessStatus.UPLOADING);

            const uploadResult = await this._uploadService.upload({ file, contentType }, (progress) => {
                this._updateFileProgress(fileInfo.id, progress, 0);
            });

            this._updateFileUploadResult(fileInfo.id, uploadResult);
            this._updateFileStatus(fileInfo.id, FileProcessStatus.UPLOAD_SUCCESS);

            // 第二阶段：识别
            this._updateFileStatus(fileInfo.id, FileProcessStatus.RECOGNIZING);

            const recognitionResult = await this._recognitionService.recognize(
                {
                    fileId: uploadResult.fileId,
                    contentType: contentType,
                },
                (progress) => {
                    this._updateFileProgress(fileInfo.id, 100, progress);
                },
            );

            this._updateFileRecognitionResult(fileInfo.id, recognitionResult);
            this._updateFileStatus(fileInfo.id, FileProcessStatus.RECOGNITION_SUCCESS);

            return fileInfo.id;
        } catch (error) {
            this._handleProcessError(fileInfo.id, error);
            throw error;
        }
    }

    async retryFile(fileId: string): Promise<void> {
        const fileInfo = this._validateFileForRetry(fileId);

        if (fileInfo.status === FileProcessStatus.UPLOAD_FAILED) {
            await this._handleUploadFailedRetry(fileId, fileInfo);
        } else if (fileInfo.status === FileProcessStatus.RECOGNITION_FAILED) {
            await this._handleRecognitionFailedRetry(fileId, fileInfo);
        }
    }

    private _validateFileForRetry(fileId: string): FileInfo {
        const fileInfo = this._files.get(fileId);
        if (!fileInfo) {
            throw new Error(`File with id ${fileId} not found`);
        }

        if (!this._isRetryableStatus(fileInfo.status)) {
            throw new Error(`File ${fileId} is not in a retry-able state`);
        }

        return fileInfo;
    }

    private _isRetryableStatus(status: FileProcessStatus): boolean {
        return status === FileProcessStatus.UPLOAD_FAILED ||
               status === FileProcessStatus.RECOGNITION_FAILED;
    }

    private async _handleUploadFailedRetry(fileId: string, fileInfo: FileInfo): Promise<void> {
        if (!fileInfo.originalFile) {
            throw new Error('Cannot retry file upload: original file object not available');
        }
        await this._retryFullProcess(fileId, fileInfo);
    }

    private async _handleRecognitionFailedRetry(fileId: string, fileInfo: FileInfo): Promise<void> {
        if (!fileInfo.uploadResult) {
            throw new Error('Upload result not available for recognition retry');
        }
        await this._retryRecognition(fileId, fileInfo);
    }

    private async _retryFullProcess(fileId: string, fileInfo: FileInfo): Promise<void> {
        if (!fileInfo.originalFile) {
            throw new Error('Original file not available for retry');
        }

        try {
            // 重置状态和进度
            this._updateFileProgress(fileId, 0, 0);
            this._updateFileStatus(fileId, FileProcessStatus.UPLOADING);

            // 重新上传
            const uploadResult = await this._uploadService.upload(
                { file: fileInfo.originalFile, contentType: fileInfo.contentType },
                (progress) => {
                    this._updateFileProgress(fileId, progress, 0);
                },
            );

            this._updateFileUploadResult(fileId, uploadResult);
            this._updateFileStatus(fileId, FileProcessStatus.UPLOAD_SUCCESS);

            // 重新识别
            this._updateFileStatus(fileId, FileProcessStatus.RECOGNIZING);

            const recognitionResult = await this._recognitionService.recognize(
                {
                    fileId: uploadResult.fileId,
                    contentType: fileInfo.contentType,
                },
                (progress) => {
                    this._updateFileProgress(fileId, 100, progress);
                },
            );

            this._updateFileRecognitionResult(fileId, recognitionResult);
            this._updateFileStatus(fileId, FileProcessStatus.RECOGNITION_SUCCESS);
        } catch (error) {
            this._handleProcessError(fileId, error);
            throw error;
        }
    }

    private _handleProcessError(fileId: string, error: unknown): void {
        const errorMessage = error instanceof Error ? error.message : '未知错误';
        const currentFile = this._files.get(fileId);

        if (currentFile?.status === FileProcessStatus.UPLOADING) {
            this._updateFileStatus(fileId, FileProcessStatus.UPLOAD_FAILED, errorMessage);
        } else if (currentFile?.status === FileProcessStatus.RECOGNIZING) {
            this._updateFileStatus(fileId, FileProcessStatus.RECOGNITION_FAILED, errorMessage);
        }
    }

    private async _retryRecognition(fileId: string, fileInfo: FileInfo): Promise<void> {
        if (!fileInfo.uploadResult) {
            throw new Error('Upload result not available for recognition retry');
        }

        try {
            this._updateFileStatus(fileId, FileProcessStatus.RECOGNIZING);

            const recognitionResult = await this._recognitionService.recognize(
                {
                    fileId: fileInfo.uploadResult.fileId,
                    contentType: fileInfo.contentType,
                },
                (progress) => {
                    this._updateFileProgress(fileId, 100, progress);
                },
            );

            this._updateFileRecognitionResult(fileId, recognitionResult);
            this._updateFileStatus(fileId, FileProcessStatus.RECOGNITION_SUCCESS);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '未知错误';
            this._updateFileStatus(fileId, FileProcessStatus.RECOGNITION_FAILED, errorMessage);
            throw error;
        }
    }

    async removeFile(contentType: 'DISCLOSURE' | 'CLAIM'): Promise<void> {
        // 从服务端删除文件
        const taskId = this._currentTaskService.taskId;
        if (taskId) {
            try {
                await this._fileDeleteService.deleteFile(taskId, contentType);
            } catch (error) {
                console.error('Failed to delete file from server:', error);
                // 即使服务端删除失败，也继续删除本地文件
            }
        }

        // 根据内容类型删除相应的本地文件
        const filesToRemove = Array.from(this._files.entries()).filter(([, fileInfo]) => {
            return fileInfo.contentType === contentType;
        });

        for (const [fileId] of filesToRemove) {
            this._files.delete(fileId);
        }

        if (filesToRemove.length > 0) {
            this._onFilesChangedEmitter.fire(this.getAllFiles());
        }
    }

    private _updateFileStatus(
        fileId: string,
        status: FileProcessStatus,
        error?: string,
    ): void {
        const fileInfo = this._files.get(fileId);
        if (!fileInfo) return;

        const updatedFileInfo = {
            ...fileInfo,
            status,
            ...this._getErrorFieldsForStatus(status, error),
        };

        this._files.set(fileId, updatedFileInfo);
        this._onFilesChangedEmitter.fire(this.getAllFiles());
    }

    private _getErrorFieldsForStatus(status: FileProcessStatus, error?: string): Partial<FileInfo> {
        if (status === FileProcessStatus.UPLOAD_FAILED && error) {
            return { uploadError: error, recognitionError: undefined };
        }

        if (status === FileProcessStatus.RECOGNITION_FAILED && error) {
            return { recognitionError: error };
        }

        if (status === FileProcessStatus.RECOGNITION_SUCCESS) {
            return { uploadError: undefined, recognitionError: undefined };
        }

        return {};
    }

    private _updateFileProgress(
        fileId: string,
        uploadProgress: number,
        recognitionProgress: number,
    ): void {
        const fileInfo = this._files.get(fileId);
        if (!fileInfo) return;

        const updatedFileInfo = {
            ...fileInfo,
            uploadProgress,
            recognitionProgress,
        };

        this._files.set(fileId, updatedFileInfo);
        // 🔧 关键修复：进度变化时也要触发文件列表更新事件
        this._onFilesChangedEmitter.fire(this.getAllFiles());
    }

    private _updateFileUploadResult(fileId: string, result: UploadResult): void {
        const fileInfo = this._files.get(fileId);
        if (!fileInfo) return;

        const updatedFileInfo = { ...fileInfo, uploadResult: result };
        this._files.set(fileId, updatedFileInfo);
        // 🔧 上传结果更新时也触发文件列表更新事件
        this._onFilesChangedEmitter.fire(this.getAllFiles());
    }

    private _updateFileRecognitionResult(fileId: string, result: RecognitionResult): void {
        const fileInfo = this._files.get(fileId);
        if (!fileInfo) return;

        const updatedFileInfo = { ...fileInfo, recognitionResult: result };
        this._files.set(fileId, updatedFileInfo);
        // 🔧 识别结果更新时也触发文件列表更新事件
        this._onFilesChangedEmitter.fire(this.getAllFiles());
    }

}
